// Funções puras do envio direto de documentos ao Azure Blob (Put Blob / Put Block + Put Block List)
// e das validações feitas ANTES de pedir a SAS. Ficam fora de documentUpload.ts para os testes não
// carregarem o authService (MSAL). Adaptado do mediaUploadUtils.ts do marketingwebui.

/** Até este tamanho o arquivo vai num único Put Blob (limite do serviço: 5000 MiB; 64 MiB é seguro p/ retry). */
export const SINGLE_PUT_MAX_BYTES = 64 * 1024 * 1024;
/** Tamanho de cada bloco no Put Block. */
export const BLOCK_SIZE_BYTES = 8 * 1024 * 1024;
/** Novas tentativas por bloco (além da primeira). */
export const BLOCK_MAX_RETRIES = 3;
/** Limite aceito pela API (POST /documents/uploads): 50 MB. */
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;

/** Extensões aceitas pela API (POST /documents/uploads). */
export const DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'heic', 'webp', 'doc', 'docx', 'xls', 'xlsx', 'txt'] as const;

/** Valor do atributo `accept` do input de arquivo. */
export const DOCUMENT_ACCEPT = DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(',');

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
};

export const extensionOf = (fileName: string): string => {
  const i = fileName.lastIndexOf('.');
  return i < 0 ? '' : fileName.slice(i + 1).toLowerCase();
};

/** "Comprovante.PDF" → "Comprovante" (título padrão do documento). */
export const titleFromFileName = (fileName: string): string => {
  const i = fileName.lastIndexOf('.');
  const base = i <= 0 ? fileName : fileName.slice(0, i);
  return base.trim() || fileName;
};

/**
 * Content-Type do arquivo pela extensão (fonte confiável: vários navegadores deixam `File.type`
 * vazio para .heic/.docx) — cai no tipo do navegador e, por fim, em octet-stream.
 */
export const guessContentType = (fileName: string, browserType?: string): string =>
  MIME_BY_EXTENSION[extensionOf(fileName)] ?? (browserType || '') ?? 'application/octet-stream';

/** Mensagem de erro (pt-BR) se o arquivo não pode ser enviado; `null` quando está ok. */
export const validateDocumentFile = (file: { name: string; size: number }): string | null => {
  const ext = extensionOf(file.name);
  if (!(DOCUMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return `Tipo de arquivo não aceito (.${ext || '?'}). Aceitos: ${DOCUMENT_EXTENSIONS.map((e) => e.toUpperCase()).join(', ')}.`;
  }
  if (file.size <= 0) return 'O arquivo está vazio.';
  if (file.size > DOCUMENT_MAX_BYTES) return 'O arquivo passa do limite de 50 MB.';
  return null;
};

export type UploadMode = 'single' | 'blocks';

export const uploadModeFor = (size: number): UploadMode => (size <= SINGLE_PUT_MAX_BYTES ? 'single' : 'blocks');

export interface ChunkPlan {
  index: number;
  start: number;
  /** Exclusivo (como em `Blob.slice`). */
  end: number;
}

/** Divide `size` bytes em blocos de `blockSize` (o último pode ser menor). Arquivo vazio → []. */
export const planChunks = (size: number, blockSize: number = BLOCK_SIZE_BYTES): ChunkPlan[] => {
  if (!(size > 0) || !(blockSize > 0)) return [];
  const chunks: ChunkPlan[] = [];
  for (let index = 0, start = 0; start < size; index++, start += blockSize) {
    chunks.push({ index, start, end: Math.min(start + blockSize, size) });
  }
  return chunks;
};

/**
 * Id do bloco: base64 de um texto de largura FIXA (o Blob exige que todos os ids de um blob
 * tenham o mesmo tamanho antes da codificação).
 */
export const blockIdFor = (index: number): string => btoa(`block-${String(index).padStart(6, '0')}`);

/** Acrescenta parâmetros à URL SAS (que já tem `?sv=…&sig=…`). */
export const appendQuery = (url: string, query: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}${query}`;

/** URL do Put Block — o id em base64 precisa ir codificado (`+`, `/`, `=`). */
export const putBlockUrl = (sasUrl: string, blockId: string): string =>
  appendQuery(sasUrl, `comp=block&blockid=${encodeURIComponent(blockId)}`);

export const putBlockListUrl = (sasUrl: string): string => appendQuery(sasUrl, 'comp=blocklist');

/** Corpo XML do Put Block List com os ids na ordem. */
export const blockListXml = (blockIds: string[]): string =>
  `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds
    .map((id) => `<Latest>${id}</Latest>`)
    .join('')}</BlockList>`;

/** Bytes → hexadecimal minúsculo. */
export const toHex = (bytes: ArrayBuffer | Uint8Array): string => {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < view.length; i++) out += view[i].toString(16).padStart(2, '0');
  return out;
};

/**
 * SHA-256 (hex) de um buffer, pelo WebCrypto do navegador. Vai no `POST /documents/:id/complete`;
 * o backend só registra (a conferência é por existência e tamanho do blob).
 */
export const sha256Hex = async (data: ArrayBuffer | Uint8Array): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error('Este navegador não suporta o cálculo de SHA-256 (WebCrypto).');
  const digest = await subtle.digest('SHA-256', data as BufferSource);
  return toHex(digest);
};

/** Erro de cancelamento pelo usuário (não é falha). */
export class UploadCancelledError extends Error {
  constructor() {
    super('Envio cancelado');
    this.name = 'UploadCancelledError';
  }
}

export const isUploadCancelled = (err: unknown): boolean =>
  err instanceof UploadCancelledError || (err instanceof DOMException && err.name === 'AbortError');
